import {
  Box,
  VStack,
  HStack,
  Text,
  Avatar,
  Input,
  IconButton,
  Divider,
  useToast,
  Tooltip,
} from '@chakra-ui/react'
import { useState } from 'react'
import { MdSend, MdDelete } from 'react-icons/md'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { IImageZone } from '../../../types/image-zone'
import { IComment } from '../../../types/comments'
import {
  useAddCommentToZone,
  useGetZoneComments,
  useDeleteCommentFromZone,
} from '../../../api/api_hooks/comments'
import { useMyProfile } from '../../../api/api_hooks/user/myProfile'
import { getSavedAuthData } from '../../../api/axios/interceptor'

interface AnnotationThreadProps {
  imageZone: IImageZone
  annotationNumber: number
  onResolve?: (zoneId: string, resolved: boolean) => void
  isResolved?: boolean
  onZoneDeleted?: () => void
}

export default function AnnotationThread({
  imageZone,
  annotationNumber,
  onResolve,
  isResolved = false,
  onZoneDeleted,
}: AnnotationThreadProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [newComment, setNewComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // Get current user
  const { data: currentUser } = useMyProfile()

  // Get comments for this image zone
  const { result: comments = [], refetch: refetchComments } = useGetZoneComments(imageZone.id)

  // Mutations for adding and deleting comments
  const { mutateAsync: addComment } = useAddCommentToZone()
  const { mutateAsync: deleteComment } = useDeleteCommentFromZone()

  const handleAddComment = async () => {
    if (!newComment.trim()) return

    setIsSubmitting(true)
    try {
      await addComment({ imageZoneId: imageZone.id, comment: newComment.trim() })
      setNewComment('')

      // Invalidate and refetch to get the new comment from server
      const { userId } = getSavedAuthData()
      const commentsQueryKey = `/image-zones/${imageZone.id}/comments${userId}`
      await queryClient.invalidateQueries({ queryKey: [commentsQueryKey] })

      // Force refetch to get fresh data including the new comment
      await refetchComments()

      toast({
        title: t('whiteboard.annotations.commentAdded'),
        status: 'success',
        duration: 2000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: t('shared.errors.errorMsg'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAddComment()
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (!commentId) return

    setDeletingId(commentId)
    try {
      await deleteComment({
        commentId,
        imageZoneId: imageZone.id,
        diagnosticImageId: imageZone.diagnosticImageId,
        diagnosticId: imageZone.diagnosticImage?.diagnosticId,
      })

      // Update cache directly by removing the deleted comment
      const { userId } = getSavedAuthData()
      const commentsQueryKey = `/image-zones/${imageZone.id}/comments${userId}`

      // Get current comments from cache and remove the deleted one
      const currentComments = queryClient.getQueryData<typeof comments>([commentsQueryKey])
      const updatedComments = (currentComments || []).filter((c: any) => c.id !== commentId)

      // Update cache with filtered comments
      queryClient.setQueryData([commentsQueryKey], updatedComments)

      // If no comments remain, the backend has deleted the zone
      // Notify parent to close modal and refresh zones
      if (updatedComments.length === 0) {
        // Also update image zones cache since the zone was deleted
        if (imageZone.diagnosticImage?.diagnosticId && imageZone.diagnosticImageId) {
          const imageZonesQueryKey = `diagnostics/${imageZone.diagnosticImage.diagnosticId}/diagnostic-images/${imageZone.diagnosticImageId}/image-zones${userId}`

          // Remove the deleted zone from cache
          const currentZones = queryClient.getQueryData<any[]>([imageZonesQueryKey])
          if (currentZones) {
            const updatedZones = currentZones.filter((z: any) => z.id !== imageZone.id)
            queryClient.setQueryData([imageZonesQueryKey], updatedZones)
          }
        }
        onZoneDeleted?.()
      }

      toast({
        title: t('whiteboard.annotations.commentDeleted'),
        status: 'success',
        duration: 2000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: t('shared.errors.errorMsg'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <VStack spacing={3} align="stretch" w="100%">

      {/* Comments list */}
      <VStack spacing={3} align="stretch">
        {comments.length === 0 ? (
          <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
            {t('whiteboard.annotations.noAnnotations')}
          </Text>
        ) : (
          comments.map((comment: IComment) => (
            <Box
              key={comment.id}
              p={3}
              bg={comment.doctorId === currentUser?.id ? 'purple.50' : 'gray.50'}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={comment.doctorId === currentUser?.id ? 'purple.200' : 'gray.200'}
            >
              <HStack spacing={3} align="start">
                <Avatar
                  size="sm"
                  name={comment.owner?.contactName}
                  bg="brand.purple"
                />
                <VStack spacing={1} align="start" flex={1}>
                  <HStack spacing={2} w="100%" justify="space-between">
                    <HStack spacing={2}>
                      <Text fontSize="sm" fontWeight="600" color="gray.700">
                        {comment.owner?.contactName}
                      </Text>
                      {comment.owner?.specialty && (
                        <Text fontSize="xs" color="gray.500">
                          • {comment.owner.specialty.name}
                        </Text>
                      )}
                    </HStack>
                    {comment.doctorId === currentUser?.id && (
                      <Tooltip label={t('whiteboard.annotations.deleteComment')}>
                        <IconButton
                          aria-label="delete-comment"
                          icon={<MdDelete />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeleteComment(comment.id)}
                          isDisabled={deletingId === comment.id}
                          isLoading={deletingId === comment.id}
                        />
                      </Tooltip>
                    )}
                  </HStack>
                  <Text fontSize="xs" color="gray.500">
                    {moment(comment.createdAt).fromNow()}
                  </Text>
                  <Text fontSize="sm" color="gray.800" mt={1}>
                    {comment.commentText}
                  </Text>
                </VStack>
              </HStack>
            </Box>
          ))
        )}
      </VStack>

      <Divider />

      {/* Add comment input */}
      <HStack spacing={2}>
        <Input
          placeholder={
            comments.length === 0
              ? t('whiteboard.annotations.writeComment')
              : t('whiteboard.annotations.writeReply')
          }
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyPress={handleKeyPress}
          size="sm"
          borderRadius="lg"
          disabled={isSubmitting}
        />
        <IconButton
          aria-label="send-comment"
          icon={<MdSend />}
          colorScheme="purple"
          size="sm"
          onClick={handleAddComment}
          isLoading={isSubmitting}
          isDisabled={!newComment.trim()}
        />
      </HStack>
    </VStack>
  )
}
